// src/components/TemplatesTab.js
// ─────────────────────────────────────────────────────────────
//  Vendor admin — "🗂 Templates" tab. Manage reusable ModifierTemplates:
//  list, create blank, create from an existing item (the key shortcut),
//  edit (propagates to every bundle referencing it), delete (blocked
//  while in use by a bundle).
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import api from '../services/api';
import TemplateEditor from './TemplateEditor';

export default function TemplatesTab({ menu }) {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [templates,    setTemplates]    = useState([]);
  const [usageByTpl,   setUsageByTpl]   = useState({}); // templateId -> [{id,name,component,isActive}]
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [editing,      setEditing]      = useState(null); // template object, or {} for blank new
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState('');
  const [picking,      setPicking]      = useState(false); // "create from item" picker open

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendors/me/templates');
      setTemplates(r.data || []);
      // Fetch usage for each template so the list can show "Used by N bundles"
      // and the delete button can be disabled up front.
      const usagePairs = await Promise.all(
        (r.data || []).map(t => api.get(`/vendors/me/templates/${t.id}/usage`).then(res => [t.id, res.data]).catch(() => [t.id, []]))
      );
      setUsageByTpl(Object.fromEntries(usagePairs));
    } catch (_) { showMsg('❌ Failed to load templates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = templates.filter(t => !search.trim() || t.name.toLowerCase().includes(search.trim().toLowerCase()));
  const simpleItems = (menu || []).filter(i => i.itemType !== 'BUNDLE');

  const handleSaveTemplate = async ({ id, name, groups }) => {
    setSaving(true);
    try {
      if (id) {
        await api.patch(`/vendors/me/templates/${id}`, { name, groups });
        showMsg('✅ Template updated — changes applied to all bundles using it');
      } else {
        await api.post('/vendors/me/templates', { name, groups });
        showMsg('✅ Template created');
      }
      setEditing(null);
      load();
    } catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Save failed')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (template) => {
    const usage = usageByTpl[template.id] || [];
    if (usage.length > 0) {
      showMsg(`❌ Can't delete — used by: ${usage.map(u => u.name).join(', ')}`);
      return;
    }
    try {
      await api.delete(`/vendors/me/templates/${template.id}`);
      showMsg('✅ Deleted');
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        showMsg(`❌ Used by: ${(err.response.data.bundles || []).map(b => b.name).join(', ')}`);
      } else {
        showMsg('❌ Delete failed');
      }
    }
  };

  return (
    <div>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      {msg && <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13, background:msg.startsWith('✅')?'#1a3d2a':'#3d1a1a', color:msg.startsWith('✅')?'var(--green)':'var(--red)' }}>{msg}</div>}

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search templates..." style={{ flex:1, minWidth:200 }} />
        <button className="btn btn-primary" onClick={() => setEditing({})}>＋ Create Template</button>
        <button className="btn btn-ghost" onClick={() => setPicking(true)}>＋ Create from existing item</button>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
          Templates ({filtered.length})
        </div>
        {loading && <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ padding:40, textAlign:'center', color:'var(--text3)' }}>
            No templates yet. Create one or copy from an existing item's modifiers.
          </div>
        )}
        {filtered.map(t => {
          const usage = usageByTpl[t.id] || [];
          return (
            <div key={t.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>{t.name}</div>
                <div style={{ fontSize:12, color:'var(--text2)' }}>
                  {t.groups?.length || 0} modifier group{(t.groups?.length || 0) !== 1 ? 's' : ''} · Used by {usage.length} bundle{usage.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(t)}>Edit</button>
                <button className="btn btn-danger btn-sm" disabled={usage.length > 0}
                  title={usage.length > 0 ? `In use by: ${usage.map(u => u.name).join(', ')}` : ''}
                  style={usage.length > 0 ? { opacity:0.4, cursor:'not-allowed' } : {}}
                  onClick={() => setConfirm({ message: `Delete template "${t.name}"?`, confirmLabel: 'Delete', onConfirm: () => handleDelete(t) })}>
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing !== null && (
        <TemplateEditor
          template={editing}
          usage={editing.id ? (usageByTpl[editing.id] || []) : []}
          onSave={handleSaveTemplate}
          onCancel={() => setEditing(null)}
          loading={saving}
        />
      )}

      {picking && (
        <CreateFromItemPicker
          items={simpleItems}
          onCreated={(template) => { setPicking(false); load(); setEditing(template); }}
          onCancel={() => setPicking(false)}
        />
      )}
    </div>
  );
}

// ── "Create from existing item" picker ────────────────────────
function CreateFromItemPicker({ items, onCreated, onCancel }) {
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState(null);
  const [name,     setName]     = useState('');
  const [creating, setCreating] = useState(false);
  const [err,      setErr]      = useState('');

  const filtered = items.filter(i => !search.trim() || i.name.toLowerCase().includes(search.trim().toLowerCase()));

  const pick = (item) => {
    setSelected(item);
    setName(`${item.name} Customization`);
  };

  const handleCreate = async () => {
    if (!selected || !name.trim()) return;
    setCreating(true); setErr('');
    try {
      const r = await api.post('/vendors/me/templates/from-item', { sourceItemId: selected.id, templateName: name.trim() });
      onCreated(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Could not create template'); }
    finally { setCreating(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2100, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'var(--bg2)', borderRadius:16, width:'100%', maxWidth:560, border:'1px solid var(--border)', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 24px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontWeight:700, fontSize:16 }}>＋ Create Template from Item</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>✕</button>
        </div>

        <div style={{ padding:24, overflowY:'auto' }}>
          {!selected ? (
            <>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="🔍 Search menu items..." style={{ width:'100%', marginBottom:14 }} />
              <div style={{ maxHeight:340, overflowY:'auto' }}>
                {filtered.map(item => (
                  <div key={item.id} onClick={() => pick(item)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, cursor:'pointer', marginBottom:6, background:'var(--bg3)' }}>
                    <span style={{ fontSize:20 }}>{item.emoji}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{item.name}</div>
                      <div style={{ fontSize:11, color:'var(--text2)' }}>{item.modifierGroups?.length || 0} modifier group(s)</div>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <div style={{ textAlign:'center', color:'var(--text3)', padding:20 }}>No items found</div>}
              </div>
            </>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <span style={{ fontSize:24 }}>{selected.emoji}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700 }}>{selected.name}</div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>← Choose a different item</button>
                </div>
              </div>

              <div style={{ marginBottom:14 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Preview — modifier groups that will be copied</div>
                {(selected.modifierGroups || []).length === 0 && <div style={{ fontSize:12, color:'var(--text3)' }}>This item has no modifier groups.</div>}
                {(selected.modifierGroups || []).map(g => (
                  <div key={g.id} style={{ fontSize:12, color:'var(--text2)', padding:'6px 10px', background:'var(--bg3)', borderRadius:6, marginBottom:4 }}>
                    {g.name} ({g.required ? 'req' : 'optional'}, pick {g.minSelect}–{g.maxSelect === 99 ? '∞' : g.maxSelect}) · {g.options?.length || 0} options
                  </div>
                ))}
              </div>

              <div style={{ marginBottom:14 }}>
                <div className="section-label" style={{ marginBottom:6 }}>Template Name *</div>
                <input type="text" value={name} onChange={e => setName(e.target.value)} />
              </div>

              {err && <div style={{ color:'var(--red)', fontSize:12, marginBottom:10 }}>{err}</div>}

              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-primary" disabled={creating || !name.trim()} onClick={handleCreate}>
                  {creating ? 'Creating...' : 'Create Template'}
                </button>
                <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
